import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { httpService } from "../../services/httpService";
import { useProfile } from "../../context/ProfileContext";

const fetchInvoice = async (orderId) => {
  const res = await httpService.get(
    `/invoice/getInvoiceSnapshotByOrderId/${orderId}`,
  );
  return res?.data ?? res;
};

const formatAddress = (addr) => {
  if (!addr) return "";
  if (typeof addr === "string") return addr.trim();
  if (Array.isArray(addr))
    return addr.map(formatAddress).filter(Boolean).join(", ");

  if (typeof addr === "object") {
    if (
      typeof addr.full_address === "string" &&
      addr.full_address.trim() !== ""
    )
      return addr.full_address;
    if (typeof addr.address === "string" && addr.address.trim() !== "")
      return addr.address;
    if (addr.officeAddress || addr.homeAddress) {
      const off = formatAddress(addr.officeAddress);
      const home = formatAddress(addr.homeAddress);
      return [off, home].filter(Boolean).join(", ");
    }
    if (
      addr.buildingName ||
      addr.locality ||
      addr.city ||
      addr.state ||
      addr.pincode
    ) {
      const parts = [
        addr.buildingName,
        addr.floor,
        addr.locality,
        addr.city,
        addr.state,
        addr.pincode,
      ]
        .filter(Boolean)
        .map((s) => (typeof s === "string" ? s.trim() : s));
      return parts.join(", ");
    }
    if (typeof addr.address === "object") return formatAddress(addr.address);
    return Object.values(addr)
      .filter((v) => typeof v === "string" && v.trim() !== "")
      .join(", ");
  }
  return String(addr);
};

const cleanText = (t) => formatAddress(t);

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const fmtMoney = (v) => `₹${num(v).toFixed(2)}`;

const truncate = (str, n = 25) => {
  const s = String(str || "");
  return s.length > n ? s.slice(0, n) + "..." : s;
};
const fmtDate = (d) => {
  try {
    return d ? format(new Date(d), "dd/MM/yyyy") : "—";
  } catch {
    return "—";
  }
};

const fmtTime = (d) => {
  try {
    return d ? format(new Date(d), "hh:mm a") : "—";
  } catch {
    return "—";
  }
};

const numberToWords = (numVal) => {
  const value = num(numVal);
  if (!value) return "Zero Rupees Only";
  const a = [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];
  const b = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];
  const w = (n, s) => {
    let str = n > 19 ? b[Math.floor(n / 10)] + " " + a[n % 10] : a[n];
    if (n) str += " " + s;
    return str;
  };
  const intPart = Math.floor(value);
  const decPart = Math.round((value - intPart) * 100);
  let out = "";
  out += w(Math.floor(intPart / 10000000), "Crore ");
  out += w(Math.floor((intPart / 100000) % 100), "Lakh ");
  out += w(Math.floor((intPart / 1000) % 100), "Thousand ");
  out += w(Math.floor((intPart / 100) % 10), "Hundred ");
  if (intPart > 100 && intPart % 100) out += "and ";
  out += w(Math.floor(intPart % 100), "");
  let final = "Rupees " + out.trim();
  if (decPart > 0) final += ` and ${w(decPart, "")} Paise`;
  return final + " Only";
};

const normalizeInvoice = (raw) => {
  const src = raw?.data ?? raw ?? {};
  const vendor = src.vendor ?? {};
  const billTo = src.bill_to ?? {};
  const summary = src.summary ?? {};
  const invoiceAmount = src.invoice_amount ?? {};

  const items = (src.items ?? []).map((it, i) => ({
    _id: it._id ?? String(i),
    service_name: it.description ?? "Service",
    item_type: it.service_type ?? "",
    hsn_code: it.hsn_code ?? "",
    expiry: it.expiry ?? "",
    batch_no: it.batch_no ?? "",
    quantity: num(it.quantity),
    unit_price: num(it.mrp || it.price),
    price_ex_tax: num(it.price || it.unit_price),
    gst: num(it.gst_amount),
    gst_percent: num(it.tax_percentage),
    discount: num(it.discount_amount),
    total_price: num(it.total_amount),
    products: (it.products ?? []).filter(Boolean).map((p, pi) => ({
      _id: p._id ?? `${it._id}-p${pi}`,
      product_name: p.product_name ?? p.name ?? "Product",
      hsn_code: p.hsn_code ?? "",
      expiry: p.expiry ?? "",
      batch_no: p.batch_no ?? "",
      quantity: num(p.quantity),
      unit_price: num(p.mrp || p.price),
      price_ex_tax: num(p.price || p.unit_price),
      gst: num(p.tax_amount ?? p.gst),
      gst_percent: num(p.tax_percentage),
      discount: num(
        p.applied_discount?.savings_amount ?? p.discount_amount ?? 0,
      ),
      total_price: num(p.total_price ?? p.final_price ?? 0),
    })),
    addons: (it.addons ?? []).filter(Boolean).map((a, ai) => ({
      _id: a.addon_id ?? a._id ?? `${it._id}-a${ai}`,
      name: a.name ?? a.addon_name ?? "Addon",
      hsn_code: a.hsn_code ?? "",
      expiry: a.expiry ?? "",
      batch_no: a.batch_no ?? "",
      quantity: num(a.quantity ?? 1),
      unit_price: num(a.addon_price ?? a.price),
      price_ex_tax: num(a.addon_price ?? a.price),
      gst: num(a.tax_amount ?? a.gst),
      gst_percent: num(a.tax_percentage ?? a.gst_percent),
      discount: 0,
      total_price: num(
        a.total_price ?? num(a.addon_price ?? a.price) * num(a.quantity ?? 1),
      ),
    })),
    otherCharges: (it.otherCharges ?? []).filter(Boolean).map((oc, oi) => ({
      _id: oc._id ?? `${it._id}-oc${oi}`,
      description: oc.description ?? oc.name ?? "Other Charge",
      hsn_code: "-",
      expiry: "-",
      batch_no: "-",
      quantity: 1,
      unit_price: num(oc.amount),
      price_ex_tax: num(oc.amount),
      gst: 0,
      gst_percent: 0,
      discount: 0,
      total_price: num(oc.amount),
    })),
  }));

  const isProductInvoice =
    items.length > 0 &&
    items.every((it) => String(it.item_type || "").toLowerCase() === "product");
  const totalQty = items.reduce((acc, item) => {
    let q = item.quantity;
    item.products?.forEach((p) => (q += p.quantity));
    item.addons?.forEach((a) => (q += a.quantity));
    return acc + q;
  }, 0);

  let paymentMode = "—";
  if (src.order_payments && src.order_payments.length > 0) {
    const modes = [
      ...new Set(
        src.order_payments.map((p) => p.payment_mode ?? p.mode).filter(Boolean),
      ),
    ];
    if (modes.length > 0) paymentMode = modes.join(", ");
  } else if (src.payment_mode) {
    paymentMode = src.payment_mode;
  }

  const netAmount = num(src.net_amount ?? summary.taxable_amount ?? 0);
  const totalDiscount = num(src.discount_amount ?? summary.item_discount_amount ?? 0);
  const subTotal = num(src.sub_total ?? (netAmount + totalDiscount));
  const roundOff = num(src.round_off ?? 0);
  const grandTotal = num(src.grand_total ?? summary.invoice_total ?? 0);

  return {
    order_number: src.invoice_no ?? src.order_no ?? "—",
    createdAt: src.invoice_date ?? src.createdAt ?? new Date().toISOString(),
    payment_status: String(src.status ?? "unpaid").toLowerCase(),
    vendor: {
      name: vendor.name ?? "—",
      phone: vendor.phone ?? "",
      email: vendor.email ?? "",
      gstNumber: vendor.gstin ?? "",
      addressLine: formatAddress(vendor.address),
      logo: vendor.logoDataUrl ?? vendor.logo_url ?? null,
    },
    vendor_client: {
      name: billTo.name ?? "",
      phone: billTo.phone ?? "",
      address: formatAddress(billTo.address),
    },
    items,
    is_product_invoice: isProductInvoice,
    total_qty: totalQty,
    sub_total: subTotal,
    net_amount: netAmount,
    total_discount: totalDiscount,
    total_gst: num(src.tax_amount ?? summary.gst_amount ?? 0),
    round_off: roundOff,
    grand_total: grandTotal,
    total_paid_amount: num(invoiceAmount.paid_amount ?? src.paid_amount),
    balance_amount: num(invoiceAmount.balance_amount),
    payment_mode: paymentMode,
  };
};


const extractProfile = (
  profileData,
  fallbackVendor = {},
  localOverrides = {},
) => {
  const d = profileData || {};
  const data = d.data || d;
  const v = d.vendor || data.vendor || {};
  const profile = data.profile || d.profile || {};
  const dataAddr = data.address || d.address || {};
  const photo = data.photo || d.photo || {};

  const backendOverrides =
    data.invoiceHeaderSettings || d.invoiceHeaderSettings || {};
  const overrides = { ...backendOverrides, ...localOverrides };

  const companyName =
    overrides.name ||
    v.businessName ||
    v.brand_name ||
    v.brand ||
    v.companyName ||
    v.name ||
    profile.businessName ||
    profile.brand ||
    profile.companyName ||
    profile.name ||
    data.businessName ||
    data.brand_name ||
    data.companyName ||
    data.name ||
    fallbackVendor.businessName ||
    fallbackVendor.brand_name ||
    fallbackVendor.companyName ||
    fallbackVendor.name ||
    "";

const safeLogo = (() => {
  // respect an explicit override (including "") before anything else
  if (overrides.logo_url !== undefined && overrides.logo_url !== null)
    return overrides.logo_url;
  if (overrides.logo !== undefined && overrides.logo !== null)
    return overrides.logo;

  return (
    v.logo_url ||
    v.logoDataUrl ||
    v.logo ||
    data.logo_url ||
    data.logoDataUrl ||
    data.logo ||
    profile.logo_url ||
    profile.logoDataUrl ||
    profile.logo ||
    profile.companyLogo ||
    profile.companyLogoUrl ||
    photo.profilePhoto ||
    photo.logo ||
    photo.companyLogo ||
    (Array.isArray(photo.shop) && photo.shop.length > 0 ? photo.shop[0] : "") ||
    fallbackVendor.logo_url ||
    fallbackVendor.logoDataUrl ||
    fallbackVendor.logo ||
    ""
  );
})();

  return {
    name: companyName,
    showSignature:
      overrides.showSignature !== undefined ? overrides.showSignature : true,
    phone:
      overrides.phone ||
      v.phone ||
      profile.phone ||
      data.phone ||
      fallbackVendor.phone ||
      "",
    email:
      overrides.email ||
      v.email ||
      profile.email ||
      data.email ||
      fallbackVendor.email ||
      "",
    gstin:
      overrides.gstin ||
      v.gstin ||
      profile.gstin ||
      data.gstin ||
      d.gstin ||
      fallbackVendor.gstin ||
      "",
    address:
      overrides.address ||
      formatAddress(v.address) ||
      formatAddress(dataAddr) ||
      formatAddress(fallbackVendor.address) ||
      "",
    logo: safeLogo,
    terms:
      overrides.terms ||
      v.terms ||
      profile.terms ||
      "• This is a computer-generated invoice\n• Subject to terms and conditions",
  };
};

const PencilIcon = () => (
  <svg
    className="w-3.5 h-3.5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
    />
  </svg>
);

const EditableField = ({
  value,
  onCommit,
  placeholder = "Click to edit",
  displayClassName = "",
  inputClassName = "",
  multiline = false,
  rows = 2,
  align = "left",
  isEditable = true,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");

  useEffect(() => {
    setDraft(value || "");
  }, [value]);

  const commit = () => {
    onCommit(draft);
    setEditing(false);
  };
  const cancel = () => {
    setDraft(value || "");
    setEditing(false);
  };

  const justify =
    align === "right"
      ? "justify-end"
      : align === "center"
        ? "justify-center"
        : "justify-start";
  const textAlign =
    align === "right"
      ? "text-right"
      : align === "center"
        ? "text-center"
        : "text-left";

  const hasValue =
    value &&
    String(value).trim() !== "" &&
    value !== "—" &&
    value !== "null" &&
    value !== "[object Object]";

  if (!isEditable) {
    if (!hasValue) return null;
    return (
      <div className={`flex items-center gap-1.5 w-full ${justify}`}>
        <div className={`flex-1 ${textAlign} ${displayClassName}`}>
          <span className={multiline ? "whitespace-pre-wrap" : ""}>
            {value}
          </span>
        </div>
      </div>
    );
  }

  if (editing) {
    return (
      <div className={`no-print flex items-center gap-1.5 w-full ${justify}`}>
        <button
          onClick={cancel}
          className="text-xs bg-slate-200 text-slate-600 px-2.5 py-1.5 rounded hover:bg-slate-300 font-bold shrink-0"
          title="Cancel (Esc)"
        >
          X
        </button>
        {multiline ? (
          <textarea
            autoFocus
            rows={rows}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") cancel();
              if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) commit();
            }}
            className={`flex-1 bg-transparent border-b border-blue-500 focus:outline-none resize-none ${textAlign} ${inputClassName}`}
            placeholder={placeholder}
          />
        ) : (
          <input
            autoFocus
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") commit();
              if (e.key === "Escape") cancel();
            }}
            className={`flex-1 bg-transparent border-b border-blue-500 focus:outline-none ${textAlign} ${inputClassName}`}
            placeholder={placeholder}
          />
        )}
        <button
          onClick={commit}
          className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 font-medium shrink-0"
          title="Save (Enter)"
        >
          Save
        </button>
      </div>
    );
  }

  if (!hasValue) {
    return (
      <div className={`group flex items-center gap-1.5 w-full ${justify} h-5`}>
        <button
          onClick={() => setEditing(true)}
          className="no-print opacity-0 group-hover:opacity-100 transition-opacity text-xs text-blue-500 hover:underline ml-auto"
        >
          + Add {placeholder}
        </button>
      </div>
    );
  }

  return (
    <div className={`group flex items-center gap-1.5 w-full ${justify}`}>
      <div className={`flex-1 ${textAlign} ${displayClassName}`}>
        <span className={multiline ? "whitespace-pre-wrap" : ""}>{value}</span>
      </div>
      <button
        onClick={() => setEditing(true)}
        className="no-print opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity text-gray-400 hover:text-blue-600 shrink-0 p-1 rounded"
        title={`Edit ${placeholder}`}
      >
        <PencilIcon />
      </button>
    </div>
  );
};

const EditableLogo = ({
  value,
  onCommit,
  className = "",
  isEditable = true,
}) => {
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        onCommit(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onCommit("");
  };
  const hasValue =
    value &&
    String(value).trim() !== "" &&
    value !== "null" &&
    value !== "[object Object]";

  if (!isEditable) {
    if (!hasValue) return null;
    return (
      <div className={`${className}`}>
        <div className="w-full h-full flex items-center justify-center bg-gray-50 border border-gray-200 rounded overflow-hidden">
          <img
            src={value}
            alt="Logo"
            className="max-w-full max-h-full object-contain"
          />
        </div>
      </div>
    );
  }

  if (!hasValue) {
    return (
      <div
        className={`relative group ${className} bg-gray-50 border border-gray-200 rounded flex items-center justify-center`}
      >
        <button
          onClick={() => fileInputRef.current?.click()}
          className="no-print opacity-0 group-hover:opacity-100 transition-opacity text-xs text-blue-500 font-medium"
        >
          + Add Logo
        </button>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept="image/*"
          className="hidden"
        />
      </div>
    );
  }

  return (
    <div className={`relative group ${className}`}>
      <div className="w-full h-full flex items-center justify-center bg-gray-50 border border-gray-200 rounded overflow-hidden">
        <img
          src={value}
          alt="Logo"
          className="max-w-full max-h-full object-contain"
        />
      </div>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/*"
        className="hidden"
      />
      <button
        onClick={handleClear}
        className="no-print absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-[12px] shadow-sm hover:bg-red-600 z-10"
        title="Remove Logo"
      >
        ✕
      </button>
      <button
        onClick={(e) => {
          e.stopPropagation();
          fileInputRef.current?.click();
        }}
        className="no-print absolute -bottom-2 -right-2 bg-white border border-gray-200 p-1.5 rounded-full shadow-sm text-gray-500 hover:text-blue-600 transition"
        title="Change Logo"
      >
        <PencilIcon />
      </button>
    </div>
  );
};

const PrintStyles = () => (
  <style>{`
    @media print {
      @page { size: A4; margin: 10mm; }
      html, body { background: #fff !important; margin: 0 !important; padding: 0 !important; }
      body * { visibility: hidden !important; }
      #invoice-print-area, #invoice-print-area * { visibility: visible !important; }
      #invoice-print-area { position: absolute !important; left: 0; top: 0; width: 100% !important; border: none !important; box-shadow: none !important; }
      .no-print { display: none !important; }
    }
  `}</style>
);

export default function LaserFormat({
  demoData = null,
  hideToolbar = false,
  isEditable = false,
}) {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const { profileData, fetchProfile, vendorId } = useProfile();

  const orderId =
    params.orderId ||
    searchParams.get("orderId") ||
    searchParams.get("id") ||
    "";

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [profile, setProfile] = useState({
    name: "",
    phone: "",
    email: "",
    gstin: "",
    address: "",
    logo: "",
    terms: "",
    showSignature: true,
  });
  const [localOverrides, setLocalOverrides] = useState({});

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState(false);

  const load = useCallback(async () => {
    if (demoData) {
      setInvoice(normalizeInvoice(demoData));
      setLoading(false);
      return;
    }
    if (!orderId) {
      setError("No order id provided.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await fetchInvoice(orderId);
      setInvoice(normalizeInvoice(res));
    } catch (e) {
      setError(
        e?.response?.data?.message ||
          e?.message ||
          "Failed to load invoice data.",
      );
    } finally {
      setLoading(false);
    }
  }, [orderId, demoData]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const fallbackVendor = demoData?.vendor || {};
    const extracted = extractProfile(
      profileData,
      fallbackVendor,
      localOverrides,
    );
    setProfile(extracted);
  }, [profileData, demoData, localOverrides]);

  const handleInlineSave = async (field, value) => {
    const updatedProfile = { ...profile, [field]: value };
    setProfile(updatedProfile);

    const payload = {
      name: updatedProfile.name,
      phone: updatedProfile.phone,
      email: updatedProfile.email,
      gstin: updatedProfile.gstin,
      address: updatedProfile.address,
      logo_url: updatedProfile.logo,
      terms: updatedProfile.terms,
      showSignature: updatedProfile.showSignature,
    };

    setSaving(true);
    setSaveError("");
    setSaveSuccess(false);

    try {
      const endpoint = vendorId
        ? `vendorregistration/updateInvoiceHeaderSettings/${vendorId}`
        : "vendorregistration/updateInvoiceHeaderSettings";

      await httpService.put(endpoint, payload);
      setLocalOverrides((prev) => ({ ...prev, ...payload }));
      if (typeof fetchProfile === "function") await fetchProfile();

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (e) {
      setSaveError(
        e?.response?.data?.message || e?.message || "Failed to save.",
      );
      setProfile(profile);
    } finally {
      setSaving(false);
    }
  };

  const orderNo = useMemo(
    () => (invoice?.order_number || "").replace("INV", "ORD"),
    [invoice],
  );

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 text-gray-600 font-mono">
        Loading invoice…
      </div>
    );
  if (error)
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-100 gap-3">
        <p className="text-red-600 font-mono">{error}</p>
        <button
          onClick={load}
          className="px-4 py-2 bg-black text-white rounded text-sm"
        >
          Retry
        </button>
      </div>
    );
  if (!invoice) return null;

  const customerName = cleanText(invoice.vendor_client.name);
  const showExpiry = invoice.is_product_invoice;

  return (
    <div
      className={
        hideToolbar
          ? "bg-white min-h-full"
          : "min-h-screen bg-gray-200 font-sans"
      }
    >
      <PrintStyles />

      {!hideToolbar && (
        <div className="no-print print:hidden sticky top-0 z-20 bg-white border-b border-gray-300">
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div>
                <h1 className="text-sm font-semibold text-gray-800">
                  Invoice Preview
                </h1>
                <p className="text-xs text-gray-500 font-mono">
                  Laser · A4 · Order #{orderId || "—"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="hidden sm:inline-block text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600 font-mono uppercase">
                laser
              </span>
              <button
                onClick={() => window.print()}
                className="px-4 py-1.5 text-sm rounded bg-black text-white hover:bg-gray-800"
              >
                🖨 Print
              </button>
            </div>
          </div>
        </div>
      )}

      <div
        className={
          hideToolbar ? "w-full flex justify-center" : "flex justify-center p-6"
        }
      >
        <div id="invoice-print-area">
          <div className="bg-white w-[794px] min-h-[1123px] px-10 py-8 text-[13px] text-gray-800 shadow-md">
            <div className="flex items-start gap-4 mb-6">
              <EditableLogo
                value={profile.logo}
                onCommit={(val) => handleInlineSave("logo", val)}
                className="shrink-0 w-16 h-16"
                isEditable={isEditable}
              />

              <div className="flex-1 flex flex-col items-center text-center">
                <h1 className="text-3xl font-bold tracking-widest text-gray-900 mb-2 uppercase flex items-center justify-center gap-2">
                  Invoice
                  {isEditable && saving && (
                    <span className="text-sm text-gray-400 font-normal lowercase tracking-normal">
                      (saving...)
                    </span>
                  )}
                  {isEditable && saveSuccess && (
                    <span className="text-sm text-green-600 font-normal lowercase tracking-normal">
                      (saved)
                    </span>
                  )}
                </h1>

                <EditableField
                  value={profile.name}
                  onCommit={(val) => handleInlineSave("name", val)}
                  placeholder="Business Name"
                  align="center"
                  displayClassName="text-xl font-bold text-gray-800 w-full"
                  inputClassName="text-xl font-bold text-gray-800"
                  isEditable={isEditable}
                />

                <div className="text-sm text-gray-600 mt-1 flex flex-col items-center gap-0.5 w-full max-w-sm">
                  <EditableField
                    value={profile.address}
                    onCommit={(val) => handleInlineSave("address", val)}
                    placeholder="Company Address"
                    multiline
                    rows={2}
                    align="center"
                    displayClassName="text-sm text-gray-600 w-full"
                    inputClassName="text-sm text-gray-600"
                    isEditable={isEditable}
                  />
                  <EditableField
                    value={profile.phone}
                    onCommit={(val) => handleInlineSave("phone", val)}
                    placeholder="Phone Number"
                    align="center"
                    displayClassName="text-sm text-gray-600 w-full font-bold"
                    inputClassName="text-sm text-gray-600 font-bold"
                    isEditable={isEditable}
                  />
                  <EditableField
                    value={profile.email}
                    onCommit={(val) => handleInlineSave("email", val)}
                    placeholder="Email Address"
                    align="center"
                    displayClassName="text-sm text-gray-600 w-full"
                    inputClassName="text-sm text-gray-600"
                    isEditable={isEditable}
                  />
                  <EditableField
                    value={profile.gstin}
                    onCommit={(val) => handleInlineSave("gstin", val)}
                    placeholder="GSTIN"
                    align="center"
                    displayClassName="text-gray-800 font-semibold mt-0.5 text-sm w-full"
                    inputClassName="text-sm text-gray-800 font-semibold"
                    isEditable={isEditable}
                  />
                </div>
              </div>

              <div className="shrink-0 w-16 h-16" />
            </div>

            <div className="border-t-2 border-gray-800 my-4"></div>

            <div className="flex justify-between mb-6">
              <div className="w-1/2">
                <table className="text-sm">
                  <tbody>
                    <tr>
                      <td className="py-0.5 font-semibold text-gray-600 w-28">
                        Invoice No:
                      </td>
                      <td className="py-0.5 font-medium">
                        {invoice.order_number}
                      </td>
                    </tr>
                    <tr>
                      <td className="py-0.5 font-semibold text-gray-600">
                        Order No:
                      </td>
                      <td className="py-0.5 font-medium">{orderNo}</td>
                    </tr>
                    <tr>
                      <td className="py-0.5 font-semibold text-gray-600">
                        Date & Time:
                      </td>
                      <td className="py-0.5">
                        {fmtDate(invoice.createdAt)}{" "}
                        {fmtTime(invoice.createdAt)}
                      </td>
                    </tr>
                  </tbody>
                </table>

                <div className="mt-5">
                  <h3 className="text-sm font-bold text-gray-800 uppercase mb-1">
                    Bill To:
                  </h3>
                  <p className="text-sm font-semibold">
                    {customerName || "Walk-in Customer"}
                  </p>
                  {invoice.vendor_client.phone && (
                    <p className="text-sm text-gray-700">
                      {invoice.vendor_client.phone}
                    </p>
                  )}
                  {invoice.vendor_client.address && (
                    <p className="text-xs text-gray-600 mt-1 max-w-xs">
                      {invoice.vendor_client.address}
                    </p>
                  )}
                </div>
              </div>
              <div className="w-1/2"></div>
            </div>

            <table className="w-full text-sm border-collapse mb-6">
              <thead>
                <tr className="bg-gray-100 text-gray-700 border-b border-gray-300">
                  <th
                    className={`text-left py-2 px-2 leading-tight ${showExpiry ? "w-[23%]" : "w-[35%]"}`}
                  >
                    Item Name
                    <br />
                    <span className="text-[11px] font-normal text-gray-500">
                      HSN / SAC
                    </span>
                  </th>
                  {showExpiry && (
                    <th className="text-left py-2 px-2 w-[12%] leading-tight">
                      Expiry
                      <br />
                      <span className="text-[11px] font-normal text-gray-500">
                        Batch No
                      </span>
                    </th>
                  )}
                  <th className="text-right py-2 px-2 w-[6%]">Qty</th>
                  <th className="text-right py-2 px-2 w-[12%]">MRP</th>
                  <th className="text-right py-2 px-2 w-[13%] leading-tight">
                    Disc Amt
                    <br />
                    <span className="text-[11px] font-normal text-gray-500">
                      Disc %
                    </span>
                  </th>
                  <th className="text-right py-2 px-2 w-[12%] leading-tight">
                    Tax Amt
                    <br />
                    <span className="text-[11px] font-normal text-gray-500">
                      Tax %
                    </span>
                  </th>
                  <th className="text-right py-2 px-2 w-[22%]">Total</th>
                </tr>
              </thead>
              <tbody>
                {invoice.items.map((item, i) => (
                  <React.Fragment key={item._id || i}>
                    <tr className="border-b border-gray-200">
                      <td className="py-2 px-2 align-top">
                        <div
                          className="font-semibold truncate"
                          title={item.service_name}
                        >
                          {truncate(item.service_name, 25)}
                        </div>
                        <div
                          className="text-[11px] text-gray-500 leading-tight mt-0.5 truncate"
                          title={item.hsn_code}
                        >
                          {item.hsn_code}
                        </div>
                      </td>
                      {showExpiry && (
                        <td className="py-2 px-2 align-top">
                          <div
                            className="text-[12px] text-gray-700 truncate"
                            title={item.expiry}
                          >
                            {item.expiry || "-"}
                          </div>
                          <div
                            className="text-[11px] text-gray-500 leading-tight mt-0.5 truncate"
                            title={item.batch_no}
                          >
                            {item.batch_no || "-"}
                          </div>
                        </td>
                      )}
                      <td className="py-2 px-2 text-right align-top font-bold">
                        {item.quantity}
                      </td>
                      <td className="py-2 px-2 text-right align-top font-bold">
                        {fmtMoney(item.unit_price)}
                      </td>
                      <td className="py-2 px-2 text-right align-top font-bold">
                        {(() => {
                          const gross = item.unit_price * item.quantity;
                          const effDisc = item.discount;
                          const discPct =
                            gross > 0 ? (effDisc / gross) * 100 : 0;
                          return (
                            <>
                              <div>
                                {effDisc > 0 ? `-${fmtMoney(effDisc)}` : "-"}
                              </div>
                              <div className="text-[11px] text-gray-500">
                                {discPct > 0 ? `${Math.round(discPct)}%` : "-"}
                              </div>
                            </>
                          );
                        })()}
                      </td>
                      <td className="py-2 px-2 text-right align-top font-bold">
                        <div>{fmtMoney(item.gst)}</div>
                        <div className="text-[11px] text-gray-500">
                          {Math.round(item.gst_percent)}%
                        </div>
                      </td>
                      <td className="py-2 px-2 text-right align-top font-bold">
                        {fmtMoney(item.total_price)}
                      </td>
                    </tr>

                    {item.products?.map((p, pi) => {
                      const gross = p.unit_price * p.quantity;
                      const effDisc = p.discount;
                      const discPct = gross > 0 ? (effDisc / gross) * 100 : 0;
                      return (
                        <tr
                          key={p._id || pi}
                          className="border-b border-gray-100 text-[12px] text-gray-600"
                        >
                          <td className="py-1.5 px-2 align-top pl-6">
                            <div
                              className="truncate font-medium"
                              title={p.product_name}
                            >
                              └ {truncate(p.product_name, 23)}
                            </div>
                            <div
                              className="text-[10px] text-gray-400 leading-tight mt-0.5 truncate"
                              title={p.hsn_code}
                            >
                              {p.hsn_code}
                            </div>
                          </td>
                          {showExpiry && (
                            <td className="py-1.5 px-2 align-top">
                              <div
                                className="text-[11px] text-gray-500 truncate"
                                title={p.expiry}
                              >
                                {p.expiry || "-"}
                              </div>
                              <div
                                className="text-[10px] text-gray-400 leading-tight mt-0.5 truncate"
                                title={p.batch_no}
                              >
                                {p.batch_no || "-"}
                              </div>
                            </td>
                          )}
                          <td className="py-1.5 px-2 text-right align-top font-bold">
                            {p.quantity}
                          </td>
                          <td className="py-1.5 px-2 text-right align-top font-bold">
                            {fmtMoney(p.unit_price)}
                          </td>
                          <td className="py-1.5 px-2 text-right align-top font-bold">
                            <div>
                              {effDisc > 0 ? `-${fmtMoney(effDisc)}` : "-"}
                            </div>
                            <div className="text-[10px] text-gray-500">
                              {discPct > 0 ? `${Math.round(discPct)}%` : "-"}
                            </div>
                          </td>
                          <td className="py-1.5 px-2 text-right align-top font-bold">
                            <div>{fmtMoney(p.gst)}</div>
                            <div className="text-[10px] text-gray-500">
                              {Math.round(p.gst_percent)}%
                            </div>
                          </td>
                          <td className="py-1.5 px-2 text-right align-top font-bold">
                            {fmtMoney(p.total_price)}
                          </td>
                        </tr>
                      );
                    })}

                    {item.addons?.map((a, ai) => {
                      const qty = a.quantity || 1;
                      const gross = a.unit_price * qty;
                      const effDisc = a.discount;
                      const discPct = gross > 0 ? (effDisc / gross) * 100 : 0;
                      return (
                        <tr
                          key={a._id || ai}
                          className="border-b border-gray-100 text-[12px] text-gray-600"
                        >
                          <td className="py-1.5 px-2 align-top pl-6">
                            <div
                              className="truncate font-medium"
                              title={a.name}
                            >
                              └ {truncate(a.name, 23)}
                            </div>
                            <div
                              className="text-[10px] text-gray-400 leading-tight mt-0.5 truncate"
                              title={a.hsn_code}
                            >
                              {a.hsn_code}
                            </div>
                          </td>
                          {showExpiry && (
                            <td className="py-1.5 px-2 align-top">
                              <div
                                className="text-[11px] text-gray-500 truncate"
                                title={a.expiry}
                              >
                                {a.expiry || "-"}
                              </div>
                              <div
                                className="text-[10px] text-gray-400 leading-tight mt-0.5 truncate"
                                title={a.batch_no}
                              >
                                {a.batch_no || "-"}
                              </div>
                            </td>
                          )}
                          <td className="py-1.5 px-2 text-right align-top font-bold">
                            {qty}
                          </td>
                          <td className="py-1.5 px-2 text-right align-top font-bold">
                            {fmtMoney(a.unit_price)}
                          </td>
                          <td className="py-1.5 px-2 text-right align-top font-bold">
                            <div>
                              {effDisc > 0 ? `-${fmtMoney(effDisc)}` : "-"}
                            </div>
                            <div className="text-[10px] text-gray-500">
                              {discPct > 0 ? `${Math.round(discPct)}%` : "-"}
                            </div>
                          </td>
                          <td className="py-1.5 px-2 text-right align-top font-bold">
                            <div>{fmtMoney(a.gst)}</div>
                            <div className="text-[10px] text-gray-500">
                              {Math.round(a.gst_percent)}%
                            </div>
                          </td>
                          <td className="py-1.5 px-2 text-right align-top font-bold">
                            {fmtMoney(a.total_price)}
                          </td>
                        </tr>
                      );
                    })}

                    {item.otherCharges?.map((oc, oi) => (
                      <tr
                        key={oc._id || oi}
                        className="border-b border-gray-100 text-[12px] text-gray-600"
                      >
                        <td
                          className="py-1.5 px-2 align-top pl-6 truncate font-medium"
                          title={oc.description}
                        >
                          └ {truncate(oc.description, 23)}
                        </td>
                        {showExpiry && (
                          <td className="py-1.5 px-2 align-top text-gray-400">
                            —
                          </td>
                        )}
                        <td className="py-1.5 px-2 text-right align-top font-bold">
                          1
                        </td>
                        <td className="py-1.5 px-2 text-right align-top font-bold">
                          {fmtMoney(oc.amount)}
                        </td>
                        <td className="py-1.5 px-2 text-right align-top font-bold">
                          —
                        </td>
                        <td className="py-1.5 px-2 text-right align-top font-bold">
                          —
                        </td>
                        <td className="py-1.5 px-2 text-right align-top font-bold">
                          {fmtMoney(oc.total_price)}
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>

            <div className="flex justify-between items-start mt-8">
              <div className="w-1/2 pt-3">
                <div className="font-semibold text-sm">
                  Total Quantity:{" "}
                  <span className="font-bold text-base">
                    {invoice.total_qty}
                  </span>
                </div>
              </div>

              <div className="w-[320px] border-t-2 border-gray-800 pt-3">
                {/* 🟢 SUB TOTAL & ITEM DISCOUNT (Only shows if discount > 0) */}
                {invoice.total_discount > 0 && (
                  <>
                    <div className="flex justify-between py-1 text-sm text-gray-600">
                      <span>Sub Total</span>
                      <span>{fmtMoney(invoice.sub_total)}</span>
                    </div>
                    <div className="flex justify-between py-1 text-sm text-gray-600">
                      <span>Item Discount</span>
                      <span>- {fmtMoney(invoice.total_discount)}</span>
                    </div>
                  </>
                )}

                <div className="flex justify-between py-1 text-sm font-bold">
                  <span className="text-gray-600">Net Amount</span>
                  <span>{fmtMoney(invoice.net_amount)}</span>
                </div>
                <div className="flex justify-between py-1 text-sm font-bold">
                  <span className="text-gray-600">Tax</span>
                  <span>{fmtMoney(invoice.total_gst)}</span>
                </div>

                {/* 🟢 ROUND OFF (Only shows if not 0) */}
                {invoice.round_off !== 0 && (
                  <div className="flex justify-between py-1 text-sm text-gray-600">
                    <span>Round Off</span>
                    <span>
                      {invoice.round_off > 0 ? "+" : "-"}
                      {fmtMoney(Math.abs(invoice.round_off))}
                    </span>
                  </div>
                )}

                <div className="border-t border-gray-300 my-2"></div>
                <div className="flex justify-between py-1 text-base font-bold">
                  <span>Order Amount</span>
                  <span>
                    {fmtMoney(
                      invoice.net_amount +
                        invoice.total_gst +
                        invoice.round_off,
                    )}
                  </span>
                </div>
                <div className="flex justify-between py-1 text-sm mt-1">
                  <span className="text-gray-600">Payment Status</span>
                  <span className="uppercase font-bold">
                    {invoice.payment_status === "paid"
                      ? "PAID"
                      : invoice.payment_status === "partial"
                        ? "PARTIAL"
                        : "UNPAID"}
                  </span>
                </div>
                <div className="flex justify-between py-1 text-base font-bold text-red-600 mt-1">
                  <span>Balance Due</span>
                  <span>{fmtMoney(invoice.balance_amount)}</span>
                </div>
              </div>
            </div>

            <div className="mt-6 pt-4 border-t border-gray-300 flex flex-col gap-1 text-sm">
              <div>
                <span className="font-semibold text-gray-700">
                  Total Amount in words:
                </span>
                <span className="italic text-gray-600 ml-2">
                  {numberToWords(invoice.grand_total)}
                </span>
              </div>
              <div>
                <span className="font-semibold text-gray-700">
                  Payment Mode:
                </span>
                <span className="ml-2 uppercase font-bold text-gray-800">
                  {invoice.payment_mode}
                </span>
              </div>
            </div>

            <div className="mt-8 flex justify-between items-start gap-8">
              <div className="text-[11px] text-gray-600 leading-5 w-full max-w-md">
                <div className="font-semibold text-gray-800 mb-1">
                  Important Notes:
                </div>
                <EditableField
                  value={profile.terms}
                  onCommit={(val) => handleInlineSave("terms", val)}
                  placeholder="Terms and Conditions"
                  multiline
                  rows={3}
                  displayClassName="text-[11px] text-gray-600 leading-5 whitespace-pre-wrap"
                  inputClassName="text-[11px] text-gray-600"
                  isEditable={isEditable}
                />
              </div>

              <div className="text-right shrink-0 relative group min-w-[200px] flex justify-end">
                {profile.showSignature !== false ? (
                  <div className="relative text-center w-48">
                    {isEditable && (
                      <button
                        onClick={() => handleInlineSave("showSignature", false)}
                        className="no-print absolute -top-6 -right-2 text-red-500 hover:text-red-700 opacity-0 group-hover:opacity-100 transition-opacity bg-white rounded-full p-1 shadow-sm border border-gray-200"
                        title="Remove Signature"
                      >
                        <svg
                          className="w-3.5 h-3.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M6 18L18 6M6 6l12 12"
                          ></path>
                        </svg>
                      </button>
                    )}

                    <div className="h-16" />
                    <div className="border-t border-gray-400 w-full pt-1 text-[11px] text-gray-600">
                      Authorised Signatory
                    </div>
                  </div>
                ) : (
                  isEditable && (
                    <button
                      onClick={() => handleInlineSave("showSignature", true)}
                      className="no-print text-xs text-blue-500 hover:underline bg-gray-50 p-2 rounded border border-gray-200 h-10"
                    >
                      + Add Signature
                    </button>
                  )
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
